import type { Notification } from '../services/notificationApi';
import type { ContractFormPreset } from '../components/AddContractModal';
import { getPropertyDisplayHeading } from './propertyAddress';

export const CONTRACT_REQUEST_EVENT = 'kcn:contract-request';

export function parseNotificationMetadata(
  notification: Pick<Notification, 'metadata' | 'content'> | { metadata?: unknown; content?: unknown },
): Record<string, unknown> | null {
  const raw = notification.metadata ?? notification.content ?? null;
  if (!raw) return null;

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  if (typeof raw === 'object') {
    return raw as Record<string, unknown>;
  }

  return null;
}

export function isContractRequestMetadata(metadata: Record<string, unknown> | null): boolean {
  if (!metadata) return false;
  const propertyId = metadata.property_id ?? metadata.propertyId;
  if (propertyId == null || propertyId === '') return false;

  const action = String(metadata.action || '').trim();
  if (action === 'contract_request' || action === 'contract-request') return true;

  return Boolean(metadata.tenant_name && (metadata.tenant_phone || metadata.tenant_cccd));
}

export function getContractRequestMetadata(
  notification: Pick<Notification, 'metadata' | 'content'> | { metadata?: unknown; content?: unknown },
): Record<string, unknown> | null {
  const metadata = parseNotificationMetadata(notification);
  return isContractRequestMetadata(metadata) ? metadata : null;
}

export function contractPresetFromRequestMetadata(
  detail: Record<string, unknown>,
): { preset: ContractFormPreset; title: string } | null {
  const propertyId = Number(detail.property_id ?? detail.propertyId);
  if (!Number.isFinite(propertyId) || propertyId <= 0) return null;

  const roomNumber = typeof detail.room_number === 'string' ? detail.room_number : '';
  const address =
    (typeof detail.full_address === 'string' && detail.full_address) ||
    (typeof detail.property_address === 'string' && detail.property_address) ||
    '';
  const propertyName =
    (typeof detail.property_name === 'string' && detail.property_name) ||
    (typeof detail.property_title === 'string' && detail.property_title) ||
    '';

  return {
    title: getPropertyDisplayHeading({
      title: propertyName,
      room_number: roomNumber,
      full_address: address,
    }) || `Phòng #${propertyId}`,
    preset: {
      property_id: propertyId,
      tenant_name: typeof detail.tenant_name === 'string' ? detail.tenant_name : '',
      tenant_phone: typeof detail.tenant_phone === 'string' ? detail.tenant_phone : '',
      tenant_cccd: typeof detail.tenant_cccd === 'string' ? detail.tenant_cccd : undefined,
      tenant_date_of_birth:
        typeof detail.tenant_date_of_birth === 'string' ? detail.tenant_date_of_birth : undefined,
      tenant_address: typeof detail.tenant_address === 'string' ? detail.tenant_address : undefined,
      rental_duration:
        typeof detail.rental_duration === 'number'
          ? detail.rental_duration
          : typeof detail.rental_duration === 'string'
            ? Number(detail.rental_duration)
            : undefined,
      monthly_payment:
        typeof detail.monthly_payment === 'number'
          ? detail.monthly_payment
          : typeof detail.monthly_payment === 'string'
            ? Number(detail.monthly_payment)
            : undefined,
    },
  };
}

export function dispatchContractRequest(metadata: Record<string, unknown>) {
  window.dispatchEvent(new CustomEvent(CONTRACT_REQUEST_EVENT, { detail: metadata }));
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

export function getNotificationIdFromPushData(data: Record<string, unknown> | null | undefined): number | undefined {
  if (!data) return undefined;
  const raw = data.notification_id ?? data.notificationId;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

/** Lấy metadata yêu cầu thuê từ payload FCM hoặc tải lại thông báo theo id. */
export async function resolveContractRequestFromPushData(
  source: unknown,
): Promise<{ metadata: Record<string, unknown>; notificationId?: number } | null> {
  const root = asRecord(source);
  if (!root) return null;
  const data = asRecord(root.data) ?? root;
  const notificationId = getNotificationIdFromPushData(data) ?? getNotificationIdFromPushData(root);

  const direct = getContractRequestMetadata({ metadata: data }) || getContractRequestMetadata({ metadata: root });
  if (direct) {
    return {
      metadata: { ...direct, notification_id: notificationId ?? direct.notification_id },
      notificationId,
    };
  }

  if (!notificationId) return null;

  try {
    const { notificationApi } = await import('../services/notificationApi');
    const notification = await notificationApi.getNotification(notificationId);
    const metadata = getContractRequestMetadata(notification);
    if (!metadata) return null;
    return { metadata: { ...metadata, notification_id: notificationId }, notificationId };
  } catch {
    return null;
  }
}
